/**
 * Fetch token metadata directly from Metaplex metadata account
 * This works for tokens that have on-chain metadata
 */

import { PublicKey } from '@solana/web3.js';

import { getConnection } from './connection';

const METADATA_PROGRAM_ID = new PublicKey('metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s');

/**
 * Derive the metadata account PDA for a token mint
 */
function getMetadataAccount(mint: PublicKey): PublicKey {
  const [metadataAccount] = PublicKey.findProgramAddressSync(
    [
      Buffer.from('metadata'),
      METADATA_PROGRAM_ID.toBuffer(),
      mint.toBuffer(),
    ],
    METADATA_PROGRAM_ID
  );
  return metadataAccount;
}

/**
 * Fetch and parse Metaplex metadata for a token
 */
export async function getMetaplexMetadata(mintAddress: string) {
  try {
    const connection = getConnection();
    const mint = new PublicKey(mintAddress);
    const metadataAccount = getMetadataAccount(mint);

    // Fetch the metadata account
    const accountInfo = await connection.getAccountInfo(metadataAccount);

    if (!accountInfo) {
      console.log(`No Metaplex metadata found for ${mintAddress}`);
      return null;
    }

    // Parse the metadata
    // Metaplex metadata structure (simplified):
    // - First 1 byte: key (should be 4 for Metadata)
    // - Next 32 bytes: update authority
    // - Next 32 bytes: mint
    // - Next 4 bytes + data: name (32 bytes max, null terminated)
    // - Next 10 bytes + data: symbol (10 bytes max, null terminated)
    // - Next 4 bytes + data: uri (200 bytes max, null terminated)

    const data = accountInfo.data;
    let offset = 1 + 32 + 32; // Skip key, update authority, and mint

    // Read name
    const nameLen = data.readUInt32LE(offset);
    offset += 4;
    const nameBytes = data.slice(offset, offset + nameLen);
    const name = nameBytes.toString('utf8').replace(/\0/g, '').trim();
    offset += nameLen;

    // Read symbol
    const symbolLen = data.readUInt32LE(offset);
    offset += 4;
    const symbolBytes = data.slice(offset, offset + symbolLen);
    const symbol = symbolBytes.toString('utf8').replace(/\0/g, '').trim();
    offset += symbolLen;

    // Read URI
    const uriLen = data.readUInt32LE(offset);
    offset += 4;
    const uriBytes = data.slice(offset, offset + uriLen);
    const uri = uriBytes.toString('utf8').replace(/\0/g, '').trim();

    console.log(`📦 Found Metaplex metadata for ${symbol}: ${uri}`);

    // If we have a URI, try to fetch the off-chain metadata
    let imageUrl: string | undefined;
    if (uri) {
      try {
        const response = await fetch(uri);
        if (response.ok) {
          const jsonMetadata = await response.json();
          imageUrl = jsonMetadata.image;
          console.log(`🖼️ Found image for ${symbol}: ${imageUrl}`);
        }
      } catch (error) {
        console.log(`Failed to fetch off-chain metadata from ${uri}:`, error);
      }
    }

    return {
      name,
      symbol,
      uri,
      imageUrl,
    };
  } catch (error) {
    console.error(`Error fetching Metaplex metadata for ${mintAddress}:`, error);
    return null;
  }
}